import { useEffect, useMemo, useRef, useState } from "react";
import { ref, onValue, off } from "firebase/database";

// ─── Park config ──────────────────────────────────────────────────────────
const PARK_DEFS = [
  { key: "MK", label: "MAGIC KINGDOM" },
  { key: "EPCOT", label: "EPCOT" },
  { key: "HS", label: "HOLLYWOOD" },
  { key: "AK", label: "ANIMAL KINGDOM" },
  { key: "DL", label: "DISNEYLAND" },
  { key: "CA", label: "CALI ADVENTURE" },
];

const STATE_COLORS = {
  open: "#2E7D32",
  ropeDrop: "#2E7D32",
  closingSoon: "#E6A817",
  specialEvent: "#7B1FA2",
  closed: "#C62828",
  gray: "#78909C",
};

const THIRTY_MIN = 30 * 60 * 1000;
const ONE_HOUR = 60 * 60 * 1000;

// ─── Time helpers ─────────────────────────────────────────────────────────
// IMPORTANT: every "now" in this file is server-corrected time —
// Date.now() + serverOffsetMs. The raw device clock is never used alone.

// Stable YYYY-MM-DD for a timezone, from a corrected-ms timestamp.
function dateKeyInTz(ms, tz) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: tz,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

// Previous calendar day for a YYYY-MM-DD string (UTC-safe arithmetic).
function prevDay(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

// The four date buckets to subscribe to: ET today/yesterday + PT today/
// yesterday. De-duplicated (ET and PT can land on the same date).
function getDateKeys(serverNowMs) {
  const etToday = dateKeyInTz(serverNowMs, "America/New_York");
  const ptToday = dateKeyInTz(serverNowMs, "America/Los_Angeles");
  return [...new Set([etToday, prevDay(etToday), ptToday, prevDay(ptToday)])];
}

// ─── Hours resolution ─────────────────────────────────────────────────────
//
// Look through every subscribed date bucket for this park. Return the record
// whose operating window (normal OR extended) actually covers "now".
//
// If NOTHING covers "now", return null — the park is closed. We deliberately
// do NOT fall back to a "nearest" record from another day; that was the bug
// that made closed parks show open.
function pickBestHoursRec(parkKey, byDate, nowMs) {
  let best = null;
  let bestSpan = Infinity;

  for (const dateKey of Object.keys(byDate)) {
    const rec = byDate[dateKey]?.parks?.[parkKey];
    if (!rec?.openTime || !rec?.closeTime) continue;

    const open = new Date(rec.openTime).getTime();
    const close = new Date(rec.closeTime).getTime();
    if (!Number.isFinite(open) || !Number.isFinite(close)) continue;

    const extClose = rec.extendedCloseTime
      ? new Date(rec.extendedCloseTime).getTime()
      : close;

    // Does this record's full window (through extended close) contain now?
    if (nowMs >= open && nowMs < extClose) {
      // If multiple buckets somehow cover now, prefer the tighter window.
      const span = extClose - open;
      if (span < bestSpan) {
        best = rec;
        bestSpan = span;
      }
    }
  }

  return best; // null if nothing covers now -> caller treats as closed
}

// ─── State computation (ORIGINAL logic — do not modify) ────────────────────
function computeState(rec, nowMs) {
  if (!rec || !rec.openTime || !rec.closeTime) {
    return { state: "closed", subLabel: "CLOSED", color: STATE_COLORS.closed };
  }

  const open = new Date(rec.openTime).getTime();
  const close = new Date(rec.closeTime).getTime();

  const hasExtended = !!rec.extendedOpenTime && !!rec.extendedCloseTime;
  const extOpen = hasExtended ? new Date(rec.extendedOpenTime).getTime() : null;
  const extClose = hasExtended ? new Date(rec.extendedCloseTime).getTime() : null;

  // PURPLE — inside the extended-hours window.
  if (hasExtended && nowMs >= extOpen && nowMs < extClose) {
    return {
      state: "specialEvent",
      subLabel: "SPECIAL EVENT",
      color: STATE_COLORS.specialEvent,
    };
  }

  // Inside normal operating hours.
  if (nowMs >= open && nowMs < close) {
    if (nowMs < open + THIRTY_MIN) {
      return {
        state: "ropeDrop",
        subLabel: hasExtended ? "ROPE DROP!" : "ROPE DROP!",
        color: STATE_COLORS.ropeDrop,
      };
    }

    // CLOSING SOON only when there are no extended hours tonight — otherwise
    // the park is about to flip to special-event mode, not actually close.
    if (!hasExtended && close - nowMs < ONE_HOUR) {
      return {
        state: "closingSoon",
        subLabel: "CLOSING SOON",
        color: STATE_COLORS.closingSoon,
      };
    }

    return { state: "open", subLabel: "OPEN", color: STATE_COLORS.open };
  }

  return { state: "closed", subLabel: "CLOSED", color: STATE_COLORS.closed };
}

// ─── Styles ───────────────────────────────────────────────────────────────
function pillBg(color) {
  return {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    background: color,
    borderRadius: 14,
    padding: "14px 6px",
    margin: "0 5px",
    minWidth: 0,
    transition: "background 0.4s ease",
  };
}

const labelStyle = {
  color: "#fff",
  fontFamily: "Lato, sans-serif",
  fontWeight: 800,
  fontSize: 18,
  textTransform: "uppercase",
  textAlign: "center",
  lineHeight: 1.2,
  letterSpacing: 0.5,
};

const subStyle = {
  color: "#fff",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  textTransform: "uppercase",
  textAlign: "center",
  marginTop: 3,
  letterSpacing: 0.3,
};

// ─── Component ────────────────────────────────────────────────────────────
const ParkPills = ({ database, subscribedParkKeys, style }) => {
  // serverOffsetMs: Firebase clock correction. `offsetReady` gates rendering
  // so we never compute state from the bare (possibly wrong) device clock.
  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [offsetReady, setOffsetReady] = useState(false);

  const [byDate, setByDate] = useState({});

  // 30s tick — drives both state recomputation and date-rollover detection.
  const [tick, setTick] = useState(0);

  // The date-key string currently subscribed. Subscriptions are only torn
  // down and rebuilt when THIS changes (i.e. at a midnight rollover).
  const [activeDateKeysStr, setActiveDateKeysStr] = useState("");

  const listenersRef = useRef([]);

  // ── Server time offset ──────────────────────────────────────────────
  useEffect(() => {
    if (!database) return;
    const offsetRef = ref(database, ".info/serverTimeOffset");
    const unsub = onValue(offsetRef, (snap) => {
      const v = Number(snap.val() || 0);
      if (Number.isFinite(v)) {
        setServerOffsetMs(v);
        setOffsetReady(true);
      }
    });
    return () => {
      off(offsetRef);
      if (typeof unsub === "function") unsub();
    };
  }, [database]);

  // ── 30s tick ────────────────────────────────────────────────────────
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // ── Decide which 4 date buckets we should be subscribed to ──────────
  // Recomputed every tick (and on offset change). Updates `activeDateKeysStr`
  // ONLY when the dates actually change — so on a normal tick this is a no-op
  // and the subscription effect below does not re-run. This is the fix for
  // BUG 1: the subscription is decoupled from serverOffset and from ticks
  // that do not cross a date boundary.
  useEffect(() => {
    if (!offsetReady) return; // wait for real server time before picking dates
    const serverNowMs = Date.now() + serverOffsetMs;
    const keysStr = getDateKeys(serverNowMs).join(",");
    setActiveDateKeysStr((prev) => (prev === keysStr ? prev : keysStr));
  }, [offsetReady, serverOffsetMs, tick]);

  // ── Subscribe to the 4 date buckets ─────────────────────────────────
  // Depends ONLY on `database` and `activeDateKeysStr`. It re-runs when the
  // dates roll over (midnight) — and at no other time. No early-return, so
  // every run that tears listeners down also rebuilds them.
  useEffect(() => {
    if (!database || !activeDateKeysStr) return;

    const dateKeys = activeDateKeysStr.split(",");

    dateKeys.forEach((dk) => {
      const dateRef = ref(database, "parkHours/byDate/" + dk);
      const handler = onValue(dateRef, (snap) => {
        setByDate((prev) => ({
          ...prev,
          [dk]: snap.exists() ? snap.val() : null,
        }));
      });
      listenersRef.current.push({ dateRef, handler });
    });

    return () => {
      listenersRef.current.forEach(({ dateRef, handler }) => {
        off(dateRef);
        if (typeof handler === "function") handler();
      });
      listenersRef.current = [];
    };
  }, [database, activeDateKeysStr]);

  // ── Compute pills ───────────────────────────────────────────────────
  // tick is in the dep list so state recomputes every 30s as time advances.
  const pills = useMemo(() => {
    const nowMs = Date.now() + serverOffsetMs;

    return PARK_DEFS.map(({ key, label }) => {
      const subscribed =
        subscribedParkKeys == null || subscribedParkKeys.includes(key);

      if (!subscribed) {
        return { key, label, color: STATE_COLORS.gray, subLabel: "" };
      }

      // Until real server time has arrived, render neutral (gray, no label)
      // rather than guessing from the device clock.
      if (!offsetReady) {
        return { key, label, color: STATE_COLORS.gray, subLabel: "" };
      }

      const rec = pickBestHoursRec(key, byDate, nowMs);
      const { color, subLabel } = computeState(rec, nowMs);
      return { key, label, color, subLabel };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byDate, subscribedParkKeys, serverOffsetMs, offsetReady, tick]);

  return (
    <div style={{ display: "flex", ...style }}>
      {pills.map((p) => (
        <div key={p.key} style={pillBg(p.color)}>
          <span style={labelStyle}>{p.label}</span>
          {p.subLabel && <span style={subStyle}>{p.subLabel}</span>}
        </div>
      ))}
    </div>
  );
};

export default ParkPills;
